import { useState } from 'react';
import { formatRial } from '@kia/shared';
import { api, errMsg } from '../api/client';
import { downloadText } from '../lib/download';
import { Button, Card, Field, Input, Textarea, useToast } from '../ui';

/**
 * پل سپیدار — the two-way bridge for the parallel-run (LEAVE_SEPIDAR_PLAN.md §6). IN: paste Sepidar's
 * cutover trial balance to post the opening entry (once). OUT: download the trial balance / journal as
 * CSV to reconcile against Sepidar. Finance/Admin only (mounted in the ledger screen).
 */
export function SepidarBridgePanel({ onPosted }: { onPosted?: () => void }) {
  const toast = useToast();
  const [date, setDate] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  async function download(path: string, filename: string) {
    const r = await api<string>(path);
    if (!r.ok) {
      toast(errMsg(r, 'خطا در دریافت فایل'), 'error');
      return;
    }
    downloadText(filename, r.body as string);
  }

  // Parse pasted "code,debit,credit" (comma or tab separated) lines from a Sepidar export.
  function parseRows(): { account: string; debit: number; credit: number }[] {
    return text
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [account, debit, credit] = line.split(/[,\t]/).map((c) => c.trim());
        return {
          account: account ?? '',
          debit: Number((debit ?? '0').replace(/[^0-9.-]/g, '')) || 0,
          credit: Number((credit ?? '0').replace(/[^0-9.-]/g, '')) || 0,
        };
      })
      .filter((r) => r.account);
  }

  async function postOpening() {
    const rows = parseRows();
    if (!date || rows.length === 0) {
      toast('تاریخ برش و مانده‌ها لازم است', 'error');
      return;
    }
    setBusy(true);
    const r = await api<{ plug: number }>('/api/accounting/opening', {
      method: 'POST',
      body: JSON.stringify({ date, rows }),
    });
    setBusy(false);
    if (!r.ok) {
      toast(errMsg(r, 'خطا در ثبت افتتاحیه'), 'error');
      return;
    }
    const plug = (r.body as { plug: number }).plug;
    toast(
      plug === 0
        ? 'مانده‌های افتتاحیه ثبت شد ✓'
        : `ثبت شد — اختلاف ${formatRial(plug)} به سود انباشته منظور شد`,
    );
    setText('');
    onPosted?.();
  }

  return (
    <Card title="پل سپیدار (ورود مانده‌ها / خروجی مغایرت‌گیری)">
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <Button
          variant="secondary"
          onClick={() =>
            void download('/api/accounting/export/trial-balance.csv', 'trial-balance.csv')
          }
        >
          دریافت تراز آزمایشی (CSV)
        </Button>
        <Button
          variant="secondary"
          onClick={() => void download('/api/accounting/export/journal.csv', 'journal.csv')}
        >
          دریافت دفتر روزنامه (CSV)
        </Button>
      </div>

      <p style={{ color: 'var(--ink-3)', marginTop: 0 }}>
        ورود مانده‌های افتتاحیه: تراز اختتامیهٔ سپیدار را در تاریخ برش، به‌صورت هر خط «کد، بدهکار،
        بستانکار» بچسبانید (یک‌بار). اختلاف احتمالی به «سود (زیان) انباشته» منظور می‌شود.
      </p>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))',
          gap: 12,
        }}
      >
        <Field label="تاریخ برش">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
      </div>
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={5}
        placeholder={'1002,100000000,0\n1006,40000000,0\n2001,0,30000000\n3001,0,110000000'}
        style={{
          marginTop: 8,
          direction: 'ltr',
          textAlign: 'left',
          fontVariantNumeric: 'tabular-nums',
        }}
      />
      <div style={{ marginTop: 10 }}>
        <Button onClick={() => void postOpening()} disabled={busy}>
          {busy ? 'در حال ثبت…' : 'ثبت مانده‌های افتتاحیه'}
        </Button>
      </div>
    </Card>
  );
}
